import { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "../../../lib/api";
import { normalizeTicketDetail } from "../utils/normalizer";
import CancellationPanel from "./CancellationPanel";
import { CANCELLATION_TYPE_SELECT, DEFAULT_CANCELLATION_TYPE_LABEL } from "../constants";

// The second flight of a Whole Trip cancellation, shown inside the first
// flight's ticket. It is its own booking (own PNR / TBO BookingId), so it keeps
// its own record behind the scenes and the same Cancellation panel works on it
// unchanged: Confirm TBO, charges, status. The passengers are the ones the
// customer picked in the chat (pending_passenger_ids), never chosen here: none
// listed means every passenger (full cancellation).

function pendingIdsOf(ticket) {
  let raw = ticket?.pending_passenger_ids;
  if (typeof raw === "string") {
    try { raw = JSON.parse(raw); } catch { raw = null; }
  }
  return Array.isArray(raw) && raw.length ? raw.map(String) : null;
}

// This flight's own booking (the ticket's booking_id); the lookup may also
// return the other flight's booking when both share the chat's PNR.
function ownBooking(raw, bookingId) {
  const all = Array.isArray(raw) ? raw : [...(raw?.upcoming || []), ...(raw?.current || []), ...(raw?.past || [])];
  return all.find((b) => bookingId && String(b.booking_id) === String(bookingId)) ?? all[0] ?? null;
}

export default function WholeTripFlightSection({ ticketId, label, formatINR, onChanged, onLoaded }) {
  const [ticket, setTicket] = useState(null);
  const [booking, setBooking] = useState(null);
  const [passengers, setPassengers] = useState([]);
  const [error, setError] = useState(null);
  const [cancelType, setCancelType] = useState(1);
  const [cancelTypeLabel, setCancelTypeLabel] = useState(DEFAULT_CANCELLATION_TYPE_LABEL);

  const load = useCallback(async () => {
    try {
      const [ticketRes, bookingsRes, passengersRes] = await Promise.all([
        api.post("/admin/tickets/detail", { ticket_id: ticketId }),
        api.post("/admin/tickets/bookings", { ticket_id: ticketId }),
        api.post("/admin/tickets/passengers", { ticket_id: ticketId }).catch(() => ({ data: { data: [] } })),
      ]);
      const t = normalizeTicketDetail(ticketRes.data?.data ?? null);
      setTicket(t);
      const own = ownBooking(bookingsRes.data?.data, t?.booking_id);
      setBooking(own);
      setPassengers(Array.isArray(passengersRes.data?.data) ? passengersRes.data.data : []);
      setError(null);
      onLoaded?.(t, own);
    } catch (e) {
      setError(e?.response?.data?.message || "Could not load this flight.");
    }
  }, [ticketId, onLoaded]);

  useEffect(() => {
    load();
  }, [load]);

  const passengersWithIds = useMemo(() => passengers.filter((p) => p.passenger_id != null), [passengers]);
  // The customer's selected passengers on this flight, when only some of them.
  const selectedIds = useMemo(() => {
    const pending = pendingIdsOf(ticket);
    if (!pending) return [];
    const picked = passengersWithIds.filter((p) => pending.includes(String(p.passenger_id))).map((p) => p.passenger_id);
    return picked.length && picked.length < passengersWithIds.length ? picked : [];
  }, [ticket, passengersWithIds]);
  const partial = selectedIds.length > 0;
  const typeOptions = useMemo(
    () => CANCELLATION_TYPE_SELECT.filter((o) => o.cancelType === (partial ? 2 : 1)),
    [partial]
  );
  useEffect(() => {
    setCancelType(partial ? 2 : 1);
    setCancelTypeLabel(partial ? "Partial Cancellation (Per Passenger)" : DEFAULT_CANCELLATION_TYPE_LABEL);
  }, [partial]);

  const route = booking?.origin && booking?.destination ? `${booking.origin} → ${booking.destination}` : null;

  return (
    <div className="border rounded-2xl overflow-hidden bg-white">
      <div className="bg-gray-50 px-4 py-3 text-sm font-semibold border-b">
        {label}
        {route ? ` · ${route}` : ""}
        {ticket?.pnr ? ` · PNR ${ticket.pnr}` : ""}
      </div>
      <div className="p-4">
        {error ? (
          <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">{error}</div>
        ) : !ticket ? (
          <div className="text-xs text-gray-500">Loading…</div>
        ) : (
          <CancellationPanel
            ticket={ticket}
            ticket_id={ticketId}
            booking={booking}
            passengersWithIds={passengersWithIds}
            selectedPassengers={selectedIds}
            cancelType={cancelType}
            setCancelType={setCancelType}
            cancelTypeLabel={cancelTypeLabel}
            setCancelTypeLabel={setCancelTypeLabel}
            partialCancelSelectionError={null}
            cancellationTypeSelectOptions={typeOptions}
            formatINR={formatINR}
            wholeTrip
            onTicketRefresh={async () => {
              await load();
              await onChanged?.();
            }}
          />
        )}
      </div>
    </div>
  );
}
